import { VIOLATION_PROPOSAL_REFUSAL_TOKENS } from "@njbsoft/api-client";
import { type ChangeRequestEvidenceFailure, type ChangeRequestReceipt } from "./types";

// Pure outbox state machine — no I/O. The six spec states plus the three
// terminal additions: `revoked` (assignment revoked server-side; draft and
// staged evidence are RETAINED and flagged in the UI), `blocked` (the utility
// refused the document because the submitter no longer satisfies a requirement
// it imposes, because a change request riding it lacks evidence the utility
// blocks on, or because a violation proposal riding it breaks one of the case
// drawer's rules — same retention as `revoked`, plus a way back: the cure
// returns the row to `finalized`) and `error` (deterministic rejection —
// without it a deterministic 422 would retry forever). `accepted`/
// `needs_review` mean the server outcome is known and all uploads acked but
// local evidence cleanup is still pending; `synced` means staged files are
// deleted (cleanup is separate I/O that can fail and retry independently).
export type OutboxState =
  | "finalized"
  | "sending"
  | "attachments_pending"
  | "accepted"
  | "needs_review"
  | "synced"
  | "revoked"
  | "blocked"
  | "error";

/**
 * What a `blocked` document must cure: a requirement the submitter does not
 * satisfy, with its cure (`acceptance_requirements_unmet`); a piece of evidence
 * a riding change request lacks, with the request it is missing from
 * (`change_request_evidence_unmet`); or a riding violation proposal's refused
 * member, its `id` the refusal's `field` (`violation_proposals[i].<member>`).
 * The acceptance and proposal arms share the `{ id, cure }` shape and differ by
 * the cure's value — `VIOLATION_PROPOSALS_REFUSED` names the proposal arm — and
 * the evidence arm carries no `cure` at all.
 */
export type BlockedRequirement = { id: string; cure: string } | ChangeRequestEvidenceFailure;

/** The cure a refused violation proposal's blocked requirement carries. */
export const VIOLATION_PROPOSALS_REFUSED = "violation_proposals_refused";

export interface PostOutcome {
  next: OutboxState;
  lastError?: string;
  serverSubmissionId?: string;
  /** The set id of the change request's receipt, when one rode the submission. */
  serverChangeRequestSetId?: string;
  /** Present only when `next === "blocked"` — what the tester must cure. */
  blockedRequirements?: BlockedRequirement[];
}

// Shape of the server's deterministic-error envelope as parsed by the API
// client (`res.error`). The scalar form is `{ error: "<token>" }`, plus the
// refused body member in `field` on the change-request and violation-proposal
// refusals that name one; the accumulate-don't-abort 422 form adds
// `findings: [{ rule, ... }, ...]`; the acceptance refusal repeats its token in
// `code` and enumerates `failing`.
interface PushErrorBody {
  error?: string;
  field?: string | null;
  findings?: { rule?: string }[];
}

/** The refusal envelope's shape, on the wire and in this module's guard. */
interface AcceptanceRefusalBody {
  code: "acceptance_requirements_unmet";
  failing: { id: string; cure: string }[];
}

/**
 * Whether a deterministic response is the utility's acceptance refusal rather
 * than any other rejection. The discriminator is the envelope's `code` key
 * plus an array `failing`, never the token in `error`: the other 422s on these
 * routes answer a bare `error` (a scalar token, or a findings envelope), and a
 * bare token classified as a refusal would park a regulatory document
 * `blocked` with no requirement to cure and no way back. The one deliberate
 * exception is a second discriminator class, `proposalRefusal`: a per-item
 * violation proposal token TOGETHER with a `field` naming the proposals
 * member, never the token alone. It may park because a cure exists — the
 * tester fixes or removes the named entry and sends the document again.
 */
export function isAcceptanceRefusal(status: number, body: unknown): body is AcceptanceRefusalBody {
  if (status !== 422 || body === null || typeof body !== "object") return false;
  const env = body as { code?: unknown; failing?: unknown };
  return env.code === "acceptance_requirements_unmet" && Array.isArray(env.failing);
}

/**
 * The evidence refusal's failures, or null for any other answer. The same
 * discriminator rule as `isAcceptanceRefusal`: the `code` key plus an array
 * `evidence_failing`, never the `error` token.
 */
function evidenceRefusal(status: number, body: unknown): ChangeRequestEvidenceFailure[] | null {
  if (status !== 422 || body === null || typeof body !== "object") return null;
  const env = body as { code?: unknown; evidence_failing?: unknown };
  // The entries are the server's own wire shape once the envelope is recognized.
  return env.code === "change_request_evidence_unmet" && Array.isArray(env.evidence_failing)
    ? (env.evidence_failing as ChangeRequestEvidenceFailure[])
    : null;
}

/** What either envelope refusal names, or null for any other answer. */
function blockedRefusal(status: number, body: unknown): BlockedRequirement[] | null {
  return isAcceptanceRefusal(status, body) ? body.failing : evidenceRefusal(status, body);
}

/**
 * The refused violation proposal's blocked requirement, or null for any other
 * answer. Recognized by the token AND the member together: `error` one of the
 * per-item rule tokens and `field` naming the proposals member. A per-item
 * token naming another member, a bare token, and `invalid_shape` at the
 * member (a shape the tester cannot cure) are all left to the terminal arm.
 */
function proposalRefusal(status: number, body: unknown): BlockedRequirement[] | null {
  if (status !== 422 || typeof body !== "object" || body === null) return null;
  if (!("error" in body) || !("field" in body)) return null;
  const { error, field } = body;
  if (typeof field !== "string" || !field.startsWith("violation_proposals")) return null;
  return VIOLATION_PROPOSAL_REFUSAL_TOKENS.some((token) => token === error)
    ? [{ id: field, cure: VIOLATION_PROPOSALS_REFUSED }]
    : null;
}

// Formats a deterministic push failure into a stable, greppable marker for the
// outbox `last_error` column (and the UI chip): the raw HTTP status ALWAYS,
// plus the server's machine token when the body is readable, so a "Failed" chip
// can name WHY instead of a bare status. Returns `http_<status> <token>` for a
// scalar envelope, `http_<status> <token>:<member>` for one naming the refused
// member, `http_<status> <token>:<firstRule>` for a findings envelope, and a
// bare `http_<status>` when the body is unreadable. Pure — no I/O (this module
// is network-free per the offline-fill guard).
export function formatPushError(status: number, body: unknown): string {
  const base = `http_${status}`;
  if (body === null || typeof body !== "object") return base;
  const env = body as PushErrorBody;
  if (typeof env.error !== "string" || env.error === "") return base;
  const findings = env.findings;
  const firstRule =
    Array.isArray(findings) && typeof findings[0]?.rule === "string" ? findings[0].rule : undefined;
  const detail =
    firstRule ?? (typeof env.field === "string" && env.field !== "" ? env.field : undefined);
  return detail !== undefined ? `${base} ${env.error}:${detail}` : `${base} ${env.error}`;
}

const REFUSED_PROPOSAL_MEMBER = /^violation_proposals\[(\d+)\]\.([a-z_]+)$/;
const REFUSAL_MARKER_TOKEN = /^http_422 ([^:]+):/;

/**
 * The refused violation proposal a blocked row names: its POSITION in the
 * queued payload's `violation_proposals`, the refused member and the rule's
 * token — read back from what the proposal arm of `decidePostOutcome` stored,
 * the first blocked requirement's `id` and the `http_422 <token>:<field>`
 * marker. Null for any other blocked row (an acceptance or evidence refusal)
 * and for a row that is not blocked. Pure.
 */
export function refusedViolationProposal(
  lastError: string | null,
  blocked: BlockedRequirement[] | null,
): { index: number; member: string; token: string } | null {
  const first = blocked?.[0];
  if (first === undefined || !("cure" in first) || first.cure !== VIOLATION_PROPOSALS_REFUSED) {
    return null;
  }
  const named = REFUSED_PROPOSAL_MEMBER.exec(first.id);
  const marker = REFUSAL_MARKER_TOKEN.exec(lastError ?? "");
  if (named?.[1] === undefined || named[2] === undefined || marker?.[1] === undefined) {
    return null;
  }
  return { index: Number(named[1]), member: named[2], token: marker[1] };
}

// Maps a submission-POST result onto the outbox machine. Retryable failures
// (network, 5xx, 429) return null — caller increments attempts and leaves
// the row in finalized for the next sync trigger. `errorBody` is the parsed
// deterministic-error envelope (the API client's `res.error`); it enriches the
// terminal-error `last_error` with the server's machine token.
export function decidePostOutcome(
  httpStatus: number | "network",
  body:
    | { id?: string; status?: string; change_request_receipt?: ChangeRequestReceipt | null }
    | undefined,
  stagedAttachmentCount: number,
  errorBody?: unknown,
): PostOutcome | null {
  if (httpStatus === "network" || httpStatus >= 500 || httpStatus === 429) return null;
  if (httpStatus === 410) return { next: "revoked", lastError: "assignment_revoked" };
  // Before the generic deterministic arm: the three refusals are curable, so
  // they park `blocked` with what to cure instead of terminal `error`. The two
  // envelope refusals' marker mirrors the findings envelope's `token:firstRule`
  // form — the first unmet id is what a chip names — and falls back to the
  // bare token when the refusal enumerated nothing.
  const failing = blockedRefusal(httpStatus, errorBody);
  if (failing !== null) {
    const first = failing[0];
    const token = formatPushError(422, errorBody);
    return {
      next: "blocked",
      lastError: first === undefined ? token : `${token}:${first.id}`,
      blockedRequirements: failing,
    };
  }
  // The proposal refusal's marker is the scalar envelope's own
  // `token:<field>`, which `refusedViolationProposal` reads back.
  const refusedProposal = proposalRefusal(httpStatus, errorBody);
  if (refusedProposal !== null) {
    return {
      next: "blocked",
      lastError: formatPushError(422, errorBody),
      blockedRequirements: refusedProposal,
    };
  }
  if (httpStatus === 200 || httpStatus === 201) {
    const id = body?.id;
    // The set that rode the test: its uploads route to the set's own path.
    const setId = body?.change_request_receipt?.set_id;
    const ids =
      setId === undefined
        ? { serverSubmissionId: id }
        : { serverSubmissionId: id, serverChangeRequestSetId: setId };
    switch (body?.status) {
      case "submitted":
        return stagedAttachmentCount > 0
          ? { next: "attachments_pending", ...ids }
          : {
              next: "error",
              lastError: "unexpected_submitted_without_attachments",
            };
      case "accepted":
        return { next: "accepted", ...ids };
      case "needs_review":
        return { next: "needs_review", ...ids };
      default:
        return {
          next: "error",
          lastError: `unexpected_status_${body?.status ?? "none"}`,
        };
    }
  }
  return { next: "error", lastError: formatPushError(httpStatus, errorBody) };
}

// Maps a walk-up assignment-create POST status onto the assignment outbox
// machine (queued|sending|synced|error). Mirrors decidePostOutcome's retryable
// arm: a network error (null) or a transient server condition (5xx / 429)
// retries; the createWorkAssignment idempotent-replay contract makes BOTH a
// 201 create and a 200 replay `synced`; every other (deterministic 4xx)
// response is terminal `error` — without it a 422 / 409 would retry forever.
export function decideAssignmentPostOutcome(status: number | null): "synced" | "retry" | "error" {
  if (status === null || status === 429 || status >= 500) return "retry";
  if (status === 200 || status === 201) return "synced";
  return "error";
}

/**
 * A set create's outcome. A receipt lands `uploading_evidence` while the set
 * awaits evidence and `received` otherwise; either curable refusal parks
 * `blocked` with what to cure, exactly as a test's does; `retry` returns the
 * row to `queued` and ends the run offline.
 */
export type ChangeRequestSetOutcome =
  | { next: "uploading_evidence" | "received"; receipt: ChangeRequestReceipt }
  | { next: "blocked"; failing: BlockedRequirement[] }
  | { next: "error" | "retry"; lastError: string };

// Maps a set-create POST result onto the set machine: network / 5xx / 429
// retry, a 201 create or 200 replay stores its receipt, a curable refusal
// blocks, and every other answer (409, 410, any other 4xx) is terminal.
export function decideChangeRequestSetOutcome(
  httpStatus: number | "network",
  receipt: ChangeRequestReceipt | undefined,
  errorBody?: unknown,
): ChangeRequestSetOutcome {
  if (httpStatus === "network" || httpStatus >= 500 || httpStatus === 429) {
    return { next: "retry", lastError: `change_request_set_${httpStatus}` };
  }
  if (httpStatus === 200 || httpStatus === 201) {
    if (receipt === undefined) return { next: "error", lastError: "unexpected_receipt_none" };
    const next = receipt.status === "awaiting_evidence" ? "uploading_evidence" : "received";
    return { next, receipt };
  }
  const failing = blockedRefusal(httpStatus, errorBody);
  if (failing !== null) return { next: "blocked", failing };
  return { next: "error", lastError: formatPushError(httpStatus, errorBody) };
}

// Maps the FINAL attachment-upload response (the server runs its routing
// when the set completes) onto the machine.
export function decideUploadCompletion(submissionStatus: string): OutboxState {
  if (submissionStatus === "accepted") return "accepted";
  if (submissionStatus === "needs_review") return "needs_review";
  return "error";
}

/**
 * Per-assignment chip state for the My Work rows: the LATEST visible outbox
 * row per assignment wins (rows arrive chronologically — UUIDv7 keys sort by
 * mint time, so the last write per assignment is the freshest).
 */
export function buildOutboxStates(
  rows: ReadonlyArray<{ assignmentId: string; state: OutboxState }>,
): ReadonlyMap<string, OutboxState> {
  const states = new Map<string, OutboxState>();
  for (const row of rows) states.set(row.assignmentId, row.state);
  return states;
}
